/**
 * Client-side expiration countdown, shared by the 2FA and password-recovery
 * code-verification screens. Renders the remaining MM:SS once per second and
 * enables the resend link when the deadline passes. Each tick recomputes the
 * remainder from an absolute deadline because background tabs throttle
 * (Safari: suspend) timers — a decrementing counter would freeze while the
 * tab is hidden.
 */

const DEFAULT_DURATION_SECONDS = 600;

/**
 * Formats a second count as MM:SS, clamping negatives to 00:00.
 *
 * @param {number} totalSeconds - remaining seconds
 * @returns {string} zero-padded MM:SS
 */
export const formatSeconds = (totalSeconds) => {
  const clamped = Math.max(0, totalSeconds);
  const minutes = String(Math.floor(clamped / 60)).padStart(2, '0');
  const seconds = String(clamped % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
};

/**
 * Parses the countdown's starting duration, defaulting when missing or
 * non-numeric. A genuine 0 (already expired) is preserved rather than
 * treated as falsy.
 *
 * @param {string|undefined} rawValue - the data-duration-seconds attribute value
 * @returns {number} whole seconds to count down from
 */
export const parseDurationSeconds = (rawValue) => {
  const numeric = Number(rawValue);
  return Number.isFinite(numeric) ? Math.trunc(numeric) : DEFAULT_DURATION_SECONDS;
};

/**
 * Whole seconds left until the deadline, rounded up so a partial second still
 * counts as remaining and the code never reads as expired early. Recomputing
 * from the absolute deadline keeps a late (throttled) tick on the right value.
 * Zero at the deadline, negative once past it; the caller treats <= 0 as expired.
 *
 * @param {number} deadlineMs - deadline as an epoch millisecond timestamp
 * @param {number} nowMs - current epoch millisecond timestamp
 * @returns {number} seconds remaining
 */
export const remainingSeconds = (deadlineMs, nowMs) => Math.ceil((deadlineMs - nowMs) / 1000);

/**
 * @param {object} options
 * @param {string} options.timeElementId - id of the element showing MM:SS
 * @param {string} options.resendElementId - id of the resend button/link to enable at zero
 */
export const initExpiryCountdown = ({ timeElementId, resendElementId }) => {
  const timeElement = document.getElementById(timeElementId);
  if (!timeElement) return;

  const enableResend = () => {
    const resendLink = document.getElementById(resendElementId);
    if (!resendLink) return;

    resendLink.disabled = false;
  };

  const durationSeconds = parseDurationSeconds(timeElement.dataset.durationSeconds);
  timeElement.textContent = formatSeconds(durationSeconds);

  // Already expired on load — enable resend now instead of after the first tick.
  if (durationSeconds <= 0) {
    enableResend();
    return;
  }

  const deadline = Date.now() + durationSeconds * 1000;
  let timer;
  const teardown = new AbortController();

  function tick() {
    const remaining = remainingSeconds(deadline, Date.now());
    timeElement.textContent = formatSeconds(remaining);
    if (remaining > 0) return;

    clearInterval(timer);
    teardown.abort();
    enableResend();
  }

  timer = setInterval(tick, 1000);
  // Snap the display when the tab becomes visible again instead of waiting for
  // the next (possibly still-throttled) interval tick; the AbortController
  // detaches this listener the moment the countdown ends.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) tick();
  }, { signal: teardown.signal });
};
